import { mediaList, type MediaItem, type MediaType } from "@/mocks/media";
import { newsList, type NewsItem } from "@/mocks/news";

export function getMediaById(id: string): MediaItem | undefined {
  return mediaList.find((item) => item.id === id);
}

export function getNewsById(id: string): NewsItem | undefined {
  return newsList.find((item) => item.id === id);
}

export function getRelatedMedia(current: MediaItem, limit = 6): MediaItem[] {
  const scored = mediaList
    .filter((item) => item.id !== current.id)
    .map((item) => {
      const sharedGenres = item.genres.filter((g) =>
        current.genres.includes(g),
      ).length;
      const sameType = item.type === current.type ? 1 : 0;
      const sameRegion = item.region === current.region ? 1 : 0;
      return { item, score: sharedGenres * 3 + sameType * 2 + sameRegion };
    })
    .sort((a, b) => b.score - a.score || b.item.popularity - a.item.popularity);
  return scored.slice(0, limit).map((entry) => entry.item);
}

export function getRelatedNews(current: NewsItem, limit = 4): NewsItem[] {
  return newsList
    .filter((item) => item.id !== current.id)
    .sort((a, b) => {
      const aSame = a.category === current.category ? 1 : 0;
      const bSame = b.category === current.category ? 1 : 0;
      return bSame - aSame;
    })
    .slice(0, limit);
}

export type SortKey = "published" | "rating" | "popularity";

export interface MediaFilter {
  type?: MediaType;
  genres?: string[];
  regions?: string[];
  years?: number[];
  sort?: SortKey;
}

export function filterMedia(filter: MediaFilter): MediaItem[] {
  const { type, genres = [], regions = [], years = [], sort = "published" } = filter;
  const result = mediaList.filter((item) => {
    if (type && item.type !== type) return false;
    if (genres.length && !genres.some((g) => item.genres.includes(g))) return false;
    if (regions.length && !regions.includes(item.region)) return false;
    if (years.length && !years.includes(item.year)) return false;
    return true;
  });
  return sortMedia(result, sort);
}

export function sortMedia(list: MediaItem[], sort: SortKey): MediaItem[] {
  const sorted = [...list];
  if (sort === "rating") {
    sorted.sort((a, b) => b.rating - a.rating);
  } else if (sort === "popularity") {
    sorted.sort((a, b) => b.popularity - a.popularity);
  } else {
    sorted.sort(
      (a, b) =>
        new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime(),
    );
  }
  return sorted;
}

export function searchMedia(query: string): MediaItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return sortMedia(
    mediaList.filter(
      (item) =>
        item.title.toLowerCase().includes(q) ||
        item.genres.some((g) => g.toLowerCase().includes(q)) ||
        item.region.toLowerCase().includes(q) ||
        item.director.toLowerCase().includes(q) ||
        item.actors.some((a) => a.toLowerCase().includes(q)),
    ),
    "popularity",
  );
}

export function searchNews(query: string): NewsItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return newsList.filter(
    (item) =>
      item.title.toLowerCase().includes(q) ||
      item.summary.toLowerCase().includes(q) ||
      item.tags.some((t) => t.toLowerCase().includes(q)),
  );
}

export function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, "0");
  const d = `${date.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${d}`;
}